import { libraryFilterManager, onboardingManager } from "@core/service-container";
import {
	sendAnalyticsEventAddAlternativeExercises,
	sendAnalyticsEventAddExercise,
} from "@domain/library/exercise-analytics";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { DayIndex } from "@domain/program/program-days";
import { TrackingContext } from "@ui/components/common/tracking-context-provider";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { isNotNil } from "@utils/assert";
import { useObservable } from "micro-observables";
import { Signal } from "micro-signals";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { complementaryGuide3ForceVisible, hideComplementaryGuide3 } from "@ui/pages/program-builder/quickstart/complementary-tooltip-signal";
import { alternativeGuide3ForceVisible, hideAlternativeGuide3, setAlternativeGuide3WasCompleted, alternativePanelQuickstartGuideRequested } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import {
	BeforeCapture,
	DragDropContext,
	DraggableLocation,
	DragStart,
	DragUpdate,
	DropResult,
	ResponderProvided,
	useMouseSensor,
	useKeyboardSensor,
} from "@hello-pangea/dnd";
import { useTolerantTouchSensor } from "@ui/pages/program-builder/drag-drop/use-tolerant-touch-sensor";

export type DragContent<T> = { content: T; provided: ResponderProvided };

export const DROPPABLE_ID = {
	...DayIndex,
	PreSelectedExercise: "PreSelectedExercise",
	Library: "Library",
	Alternative: "Alternative",
} as const;

export interface ExerciseDragAndDropContextProps {
	programEditor: ProgramContentEditor;
	isDragging: boolean;
	onBeforeCapture: Signal<BeforeCapture>;
	onDragUpdate: Signal<DragContent<DragUpdate>>;
	onDragStart: Signal<DragContent<DragStart>>;
	onDragEnd: Signal<DragContent<DropResult>>;
}

export const ExerciseDragAndDropContext = React.createContext<ExerciseDragAndDropContextProps | null>(null);

export const ProgramBuilderDragDropContextProvider: React.FC<{
	programEditor: ProgramContentEditor;
	children: React.ReactNode;
}> = ({ programEditor, children }) => {
	const [isDragging, setIsDragging] = useState(false);
	const filteredExercises = useObservable(libraryFilterManager.filteredExercises);
	const filteredPreSelectedExercises = useObservable(libraryFilterManager.filteredSelectedExercises);
	const onDragUpdate = useMemo(() => new Signal<DragContent<DragUpdate>>(), []);
	const onDragStart = useMemo(() => new Signal<DragContent<DragStart>>(), []);
	const onDragEnd = useMemo(() => new Signal<DragContent<DropResult>>(), []);
	const onBeforeCapture = useMemo(() => new Signal<BeforeCapture>(), []);
	const trackingContext = useContext(TrackingContext);
	const currentExercise = useObservable(libraryFilterManager.exerciseWithAlternativesEdited);

	const handleAlternativeDragAndDrop = useCallback(
		async (source: DraggableLocation, destination: DraggableLocation) => {
			switch (source.droppableId) {
				case destination.droppableId: {
					programEditor.reorderAlternativeExercises(source.index, destination.index);
					break;
				}
				case DROPPABLE_ID.PreSelectedExercise: {
					const exercise = filteredPreSelectedExercises[source.index];
					isNotNil(currentExercise) && sendAnalyticsEventAddAlternativeExercises(currentExercise, exercise);
					await programEditor.addLibraryExerciseToAlternatives(exercise, destination.index);
					break;
				}
				case DROPPABLE_ID.Library: {
					const exercise = filteredExercises[source.index];
					isNotNil(currentExercise) && sendAnalyticsEventAddAlternativeExercises(currentExercise, exercise);
					await programEditor.addLibraryExerciseToAlternatives(exercise, destination.index);
					break;
				}
				default:
					break;
			}
		},
		[currentExercise, filteredPreSelectedExercises, filteredExercises, programEditor],
	);

	const updateProgram = useCallback(
		(result: DropResult) => {
			const { source, destination } = result;
			// dropped outside the list
			if (!destination) {
				return;
			}
			if (destination.droppableId === DROPPABLE_ID.Alternative) {
				handleAlternativeDragAndDrop(source, destination);
			} else {
				const sourceSessionIndex = parseInt(source.droppableId);
				const destinationSessionIndex = parseInt(destination.droppableId);
				switch (source.droppableId) {
					case destination.droppableId: {
						programEditor.reorderSessionExercises(sourceSessionIndex, source.index, destination.index);
						break;
					}
					case DROPPABLE_ID.PreSelectedExercise:
						{
							const exercise = filteredPreSelectedExercises[source.index];
							programEditor.addExerciseToSession(exercise, destinationSessionIndex, destination.index);
							sendAnalyticsEventAddExercise(exercise, trackingContext.category);
						}
						break;
					case DROPPABLE_ID.Library:
						{
							const exercise = filteredExercises[source.index];
							programEditor.addExerciseToSession(exercise, destinationSessionIndex, destination.index);
							sendAnalyticsEventAddExercise(exercise, trackingContext.category);
							// Mark quickstart step as completed when a library exercise is added
							void onboardingManager.passStepOnboarding(
								OnboardingProgramQuickstartStep.ADD_EXERCISE_GUIDE_01,
							);
						}
						break;
					default: {
						programEditor.moveExercise(sourceSessionIndex, source.index, destinationSessionIndex, destination.index);
						break;
					}
				}
			}
		},
		[
			filteredPreSelectedExercises,
			filteredExercises,
			handleAlternativeDragAndDrop,
			programEditor,
			trackingContext.category,
		],
	);

	const contextValue = useMemo(
		() => ({ programEditor, isDragging, onDragUpdate, onDragEnd, onDragStart, onBeforeCapture }),
		[isDragging, onBeforeCapture, onDragEnd, onDragStart, onDragUpdate, programEditor],
	);

	return (
		<ExerciseDragAndDropContext.Provider
			value={contextValue}
		>
			<DragDropContext
				sensors={[useMouseSensor, useKeyboardSensor, useTolerantTouchSensor]}
				onBeforeCapture={before => {
					onBeforeCapture.dispatch(before);
				}}
				onDragStart={(content, provided) => {
					setIsDragging(true);
					onDragStart.dispatch({ content, provided });
				}}
					onDragEnd={(content, provided) => {
					setIsDragging(false);
					onDragEnd.dispatch({ content, provided });
					updateProgram(content);
							if (complementaryGuide3ForceVisible.get()) {
								const lastAdded = libraryFilterManager.lastAddedExerciseMetadata.get();
								if (lastAdded?.isComplementary && lastAdded.draggableId === content.draggableId) {
									void (async () => {
										await onboardingManager.passStepOnboarding(
											OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_03,
										);
										await onboardingManager.init();
									})();
									hideComplementaryGuide3();
								}
							}
					if (alternativeGuide3ForceVisible.get()) {
						hideAlternativeGuide3();
						setAlternativeGuide3WasCompleted();
					} else if (!alternativePanelQuickstartGuideRequested.get() && content.destination?.droppableId === DROPPABLE_ID.Alternative) {
						void (async () => {
							await onboardingManager.passStepOnboarding(OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_02);
							await onboardingManager.passStepOnboarding(OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_03);
							await onboardingManager.init();
						})();
					}
				}}
				onDragUpdate={(content, provided) => {
					onDragUpdate.dispatch({ content, provided });
				}}
			>
				{children}
			</DragDropContext>
		</ExerciseDragAndDropContext.Provider>
	);
};

